// src/components/tasks/TaskSplitModal.tsx

import React, { useState, useEffect } from 'react';
import { Task, TaskCategory, TaskPriority, TaskEnergyLevel } from '../../types/task';
import { taskService } from '../../features/task';
import { getCurrentUserId } from '../../services/auth';
import { AlertCircle, X, Flag, Battery, Trash2 } from 'lucide-react';
import { logError } from '../../utils/logger';
import { useSplitTask } from '../../hooks/useSplitTask'; // 引入新的 Hook

type StableSubtask = Partial<Task> & { _id: string };

interface TaskSplitModalProps {
    isOpen: boolean;
    onClose: () => void;
    task: Task;
    initialSubtasks: Partial<Task>[] | null; // 接收父元件傳遞的子任務
    isLoading: boolean;                      // 接收父元件傳遞的載入狀態
    error: string | null;                    // 接收父元件傳遞的錯誤資訊
    onSplitComplete: () => void;
}

// 子任務卡片元件保持不變
const SubtaskCard: React.FC<{
    subtask: Partial<Task>;
    onEdit: () => void;
    onDelete: () => void;
}> = ({ subtask, onEdit, onDelete }) => {
    // 根據類別設置卡片樣式
    const getCategoryStyle = (category: TaskCategory | undefined): string => {
        switch (category) {
            case 'work': return 'border-sky-500 bg-sky-50';
            case 'family': return 'border-green-500 bg-green-50';
            case 'health': return 'border-sky-500 bg-sky-50';
            case 'social': return 'border-pink-500 bg-pink-50';
            case 'personal': return 'border-yellow-500 bg-yellow-50';
            default: return 'border-neutral-500 bg-neutral-50';
        }
    };

    const categoryStyle = getCategoryStyle(subtask.category);

    // Priority 樣式和圖標
    const getPriorityStyle = (priority: TaskPriority | undefined): { color: string; text: string } => {
        switch (priority) {
            case '1': return { color: 'text-red-600', text: 'High' };
            case '2': return { color: 'text-yellow-600', text: 'Medium' };
            case '3': return { color: 'text-green-600', text: 'Low' };
            default: return { color: 'text-neutral-400', text: '' };
        }
    };

    // Energy 樣式和圖標
    const getEnergyStyle = (energy: TaskEnergyLevel | undefined): { color: string; text: string } => {
        switch (energy) {
            case '1': return { color: 'text-red-600', text: 'High' };
            case '2': return { color: 'text-yellow-600', text: 'Medium' };
            case '3': return { color: 'text-green-600', text: 'Low' };
            default: return { color: 'text-neutral-400', text: '' };
        }
    };

    const priorityStyle = getPriorityStyle(subtask.priority);
    const energyStyle = getEnergyStyle(subtask.energyLevel);

    return (
        <div className={`p-3 rounded-md mb-3 ${categoryStyle} group hover:shadow-md transition-shadow`}>
            <div className="flex justify-between items-start">
                <div className="flex-1 cursor-pointer" onClick={onEdit}>
                    <h3 className="font-medium text-neutral-800">{subtask.title || 'Unnamed subtask'}</h3>
                    <div className="flex gap-2 mt-2">
                        {subtask.priority && (
                            <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-white border ${priorityStyle.color.replace('text-', 'border-')}`}>
                                <Flag size={12} className={priorityStyle.color} />
                                <span className={priorityStyle.color}>{priorityStyle.text}</span>
                            </span>
                        )}
                        {subtask.energyLevel && (
                            <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-white border ${energyStyle.color.replace('text-', 'border-')}`}>
                                <Battery size={12} className={energyStyle.color} />
                                <span className={energyStyle.color}>{energyStyle.text}</span>
                            </span>
                        )}
                        {subtask.estimatedTime && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">
                                {Math.floor(subtask.estimatedTime / 60) > 0 ? `${Math.floor(subtask.estimatedTime / 60)}h` : ''}
                                {subtask.estimatedTime % 60 > 0 ? ` ${subtask.estimatedTime % 60}m` : ''}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1 ml-2">
                    {/* 編輯圖標 */}
                    <button
                        className="text-neutral-400 hover:text-neutral-600 p-1"
                        onClick={onEdit}
                        aria-label="Edit subtask"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                    </button>
                    {/* 刪除圖標 */}
                    <button
                        className="text-neutral-400 hover:text-red-500 p-1"
                        onClick={onDelete}
                        aria-label="Delete subtask"
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
};

// 子任務編輯模態框
const SubtaskEditModal: React.FC<{
    subtask: StableSubtask;
    onSave: (subtask: StableSubtask) => void;
    onClose: () => void;
}> = ({ subtask, onSave, onClose }) => {
    const [editedSubtask, setEditedSubtask] = useState({ ...subtask });

    const handleSave = () => {
        onSave(editedSubtask);
    };

    return (
        <div className="fixed inset-0 z-modal-nested bg-black bg-opacity-30 flex justify-center items-center">
            <div className="bg-white rounded-lg w-full max-w-md mx-4 p-4">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold">Edit Subtask</h3>
                    <button onClick={onClose} className="text-neutral-500">
                        <X size={20} />
                    </button>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Title</label>
                    <input
                        type="text"
                        value={editedSubtask.title || ''}
                        onChange={(e) => setEditedSubtask(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500"
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
                    <textarea
                        value={editedSubtask.notes || ''}
                        onChange={(e) => setEditedSubtask(prev => ({ ...prev, notes: e.target.value }))}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500"
                        rows={2}
                    />
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Priority</label>
                        <select
                            value={editedSubtask.priority || ''}
                            onChange={(e) => setEditedSubtask(prev => ({ ...prev, priority: e.target.value as TaskPriority }))}
                            className="w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500"
                        >
                            <option value="">Not set</option>
                            <option value="1">High</option>
                            <option value="2">Medium</option>
                            <option value="3">Low</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Category</label>
                        <select
                            value={editedSubtask.category || ''}
                            onChange={(e) => setEditedSubtask(prev => ({ ...prev, category: e.target.value as TaskCategory }))}
                            className="w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500"
                        >
                            <option value="">Uncategorized</option>
                            <option value="work">Work</option>
                            <option value="personal">Personal</option>
                            <option value="family">Family</option>
                            <option value="health">Health</option>
                            <option value="social">Social</option>
                        </select>
                    </div>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Energy Level</label>
                    <select
                        value={editedSubtask.energyLevel || ''}
                        onChange={(e) => setEditedSubtask(prev => ({ ...prev, energyLevel: e.target.value as TaskEnergyLevel }))}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500"
                    >
                        <option value="">Not set</option>
                        <option value="1">High</option>
                        <option value="2">Medium</option>
                        <option value="3">Low</option>
                    </select>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Estimated Time</label>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            value={editedSubtask.estimatedTime ? Math.floor(editedSubtask.estimatedTime / 60) : 0}
                            onChange={(e) => {
                                const hours = parseInt(e.target.value) || 0;
                                const minutes = editedSubtask.estimatedTime ? editedSubtask.estimatedTime % 60 : 0;
                                setEditedSubtask(prev => ({ ...prev, estimatedTime: hours * 60 + minutes }));
                            }}
                            className="w-20 px-2 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500"
                            min="0"
                        />
                        <span className="text-neutral-600">h</span>
                        <input
                            type="number"
                            value={editedSubtask.estimatedTime ? editedSubtask.estimatedTime % 60 : 0}
                            onChange={(e) => {
                                const minutes = parseInt(e.target.value) || 0;
                                const hours = editedSubtask.estimatedTime ? Math.floor(editedSubtask.estimatedTime / 60) : 0;
                                setEditedSubtask(prev => ({ ...prev, estimatedTime: hours * 60 + minutes }));
                            }}
                            className="w-20 px-2 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500"
                            min="0"
                            max="59"
                        />
                        <span className="text-neutral-600">min</span>
                    </div>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Target Date</label>
                    <input
                        type="date"
                        value={editedSubtask.targetDate || ''}
                        onChange={(e) => setEditedSubtask(prev => ({ ...prev, targetDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500"
                    />
                </div>

                <div className="flex justify-end mt-6">
                    <div className="space-x-2">
                        <button
                            onClick={onClose}
                            className="px-3 py-2 text-sm text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            className="px-3 py-2 text-sm text-white bg-sky-600 rounded-md hover:bg-sky-700"
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const withStableId = (subtask: Partial<Task>): Partial<Task> & { _id: string } => ({
    ...subtask,
    _id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
});

const TaskSplitModal: React.FC<TaskSplitModalProps> = ({
    isOpen,
    onClose,
    task,
    initialSubtasks,
    isLoading: externalIsLoading,
    error: externalError,
    onSplitComplete
}) => {
    const [subtasks, setSubtasks] = useState<StableSubtask[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentEditingSubtask, setCurrentEditingSubtask] = useState<StableSubtask | null>(null);

    const [showRegenerateForm, setShowRegenerateForm] = useState<boolean>(false);
    const [generationNotes, setGenerationNotes] = useState<string>('');
    const [isGenerating, setIsGenerating] = useState<boolean>(false);

    // 使用 Hook
    const splitTaskHook = useSplitTask();

    // 初始化子任務
    useEffect(() => {
        // 如果有外部提供的子任務，則使用外部子任務
        if (initialSubtasks && initialSubtasks.length > 0) {
            setSubtasks(initialSubtasks.map(withStableId));
        }

        // 同步外部載入狀態
        setIsLoading(externalIsLoading);

        // 同步外部錯誤資訊
        if (externalError) {
            setError(externalError);
        }
    }, [initialSubtasks, externalIsLoading, externalError]);

    // 重新生成子任務
    const handleRegenerate = async () => {
        if (isGenerating) return;

        setIsGenerating(true);
        setError(null);

        try {
            // 傳遞 generationNotes 給 splitTaskHook
            const result = await splitTaskHook.splitTask(task, {
                generationNotes, // 這裡的 generationNotes 是來自用戶輸入
                onSuccess: (_subtasks) => {
                    // 成功路徑：父層已透過 result 處理
                }
            });

            if (result && Array.isArray(result)) {
                setSubtasks(result.map(withStableId));
                setShowRegenerateForm(false); // 成功後隱藏表單
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '未知錯誤';
            setError(errorMessage);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleEditSubtask = (subtask: StableSubtask) => {
        setCurrentEditingSubtask(subtask);
    };

    const handleDeleteSubtask = (index: number) => {
        if (subtasks.length <= 1) {
            setError('At least one subtask is required');
            return;
        }
        setSubtasks(prev => prev.filter((_, i) => i !== index));
    };

    const handleSaveSubtaskEdit = (editedSubtask: StableSubtask) => {
        setSubtasks(prev => prev.map(s => s._id === editedSubtask._id ? editedSubtask : s));
        setCurrentEditingSubtask(null);
    };

    const handleAddSubtask = () => {
        const newSubtask = withStableId({
            title: `${task.title} - Step ${subtasks.length + 1}`,
            notes: '',
            category: task.category,
            priority: task.priority,
            energyLevel: task.energyLevel,
            estimatedTime: 30,
        });
        setSubtasks(prev => [...prev, newSubtask]);
        setCurrentEditingSubtask(newSubtask);
    };

    const handleApply = async () => {
        if (subtasks.length === 0) {
            setError('Please create at least one subtask');
            return;
        }

        const invalidSubtaskIndex = subtasks.findIndex(st => !st.title?.trim());
        if (invalidSubtaskIndex >= 0) {
            setError(`Subtask #${invalidSubtaskIndex + 1} is missing a title`);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const userId = getCurrentUserId();
            const cleanedSubtasks = subtasks.map(({ _id, ...subtask }) => subtask);

            await taskService.splitTask(task.id, cleanedSubtasks as Partial<Task>[], userId);
            onSplitComplete();
            onClose();
        } catch (error) {
            logError('Failed to save split tasks', error);
            setError('Save failed. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-modal-nested overflow-y-auto bg-neutral-900 bg-opacity-50 flex justify-center items-end sm:items-center">
            <div className="bg-white rounded-t-xl md:rounded-lg w-full sm:max-w-2xl sm:mx-auto shadow-xl transform transition-all animate-slideInUp h-modal-responsive flex flex-col">
                {/* 標題和關閉按鈕 */}
                <div className="flex justify-between items-center p-4 border-b border-neutral-200">
                    <div>
                        <h2 className="text-lg font-semibold text-neutral-800">Split Task</h2>
                        <p className="text-sm text-neutral-500">Break down into smaller tasks</p>
                    </div>
                    <button onClick={onClose} className="text-neutral-500 hover:text-neutral-700" aria-label="Close">
                        <X size={24} />
                    </button>
                </div>

                {/* 原始任務摘要 */}
                <div className="p-4 bg-sky-50 border-b border-sky-100">
                    <h3 className="font-medium text-neutral-700 mb-1">Original Task</h3>
                    <p className="text-base text-neutral-700">{task.title}</p>
                    {task.estimatedTime && (
                        <p className="text-sm text-neutral-600 mt-1">
                            Estimated time: {Math.floor(task.estimatedTime / 60)}h {task.estimatedTime % 60}m
                        </p>
                    )}
                </div>

                {/* 子任務列表 */}
                <div className="p-4 flex-1 overflow-y-auto min-h-0">
                    {isLoading ? (
                        <div className="absolute inset-0 flex flex-col justify-center items-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-4 border-sky-500 border-t-transparent mb-4"></div>
                            <span className="mt-5 text-sky-600">Analyzing tasks...</span>
                        </div>
                    ) : (
                        <div>
                            {error && (
                                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg flex items-start">
                                    <AlertCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm">{error}</span>
                                </div>
                            )}

                            {/* 重新生成按鈕 - 只有當有子任務時才顯示 */}
                            {subtasks.length > 0 && !showRegenerateForm && (
                                <div className="mb-4">
                                    <button
                                        onClick={() => setShowRegenerateForm(true)}
                                        className="text-gray-500 hover:text-sky-800 text-sm font-medium flex items-center"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Not satisfied? Regenerate subtasks
                                    </button>
                                </div>
                            )}

                            {/* 重新生成表單 - 只有當用戶點擊重新生成按鈕後才顯示 */}
                            {showRegenerateForm && (
                                <div className="mt-2 mb-4 p-4 bg-sky-50 rounded-lg border border-sky-100">
                                    <h3 className="font-medium text-neutral-700 mb-2">Generate Notes</h3>
                                    <textarea
                                        className="w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 mb-2"
                                        placeholder="Any constraints or things I should watch out for?"
                                        value={generationNotes}
                                        onChange={(e) => setGenerationNotes(e.target.value)}
                                        rows={3}
                                    />
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => setShowRegenerateForm(false)}
                                            className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-md text-sm font-medium hover:bg-neutral-200"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleRegenerate}
                                            disabled={isGenerating}
                                            className="px-4 py-2 bg-sky-600 text-white rounded-md text-sm font-medium hover:bg-sky-700 flex items-center"
                                        >
                                            {isGenerating ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Generating...
                                                </>
                                            ) : (
                                                'Regenerate'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* 子任務卡片列表 */}
                            {subtasks.map((subtask, index) => (
                                <SubtaskCard
                                    key={subtask._id}
                                    subtask={subtask}
                                    onEdit={() => handleEditSubtask(subtask)}
                                    onDelete={() => handleDeleteSubtask(index)}
                                />
                            ))}

                            <button
                                type="button"
                                className="w-full py-3 mt-4 flex items-center justify-center bg-neutral-100 hover:bg-neutral-200 rounded-lg text-neutral-600"
                                onClick={handleAddSubtask}
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Add Subtask
                            </button>
                        </div>
                    )}
                </div>

                {/* 底部操作區 */}
                <div className="flex flex-col sm:flex-row justify-between p-4 pb-safe-large border-t border-neutral-200 flex-shrink-0">
                    <div className="mb-3 sm:mb-0">
                        <p className="text-sm text-neutral-500">
                            After splitting, the original task will be closed.
                        </p>
                    </div>

                    <div className="flex space-x-3">
                        <button
                            onClick={onClose}
                            className="flex-1 sm:flex-none px-4 py-2 bg-neutral-100 text-neutral-700 rounded-md text-sm font-medium hover:bg-neutral-200 transition-colors"
                            disabled={isLoading}
                        >
                            Cancel
                        </button>

                        <button
                            onClick={handleApply}
                            className="flex-1 sm:flex-none px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-md text-sm font-medium transition-colors flex items-center justify-center"
                            disabled={isLoading || subtasks.length === 0}
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Applying...
                                </>
                            ) : (
                                'Split Task'
                            )}
                        </button>
                    </div>
                </div>

                {/* 編輯子任務模態窗 */}
                {currentEditingSubtask && (
                    <SubtaskEditModal
                        subtask={currentEditingSubtask}
                        onSave={handleSaveSubtaskEdit}
                        onClose={() => setCurrentEditingSubtask(null)}
                    />
                )}
            </div>
        </div>
    );
};

export default TaskSplitModal;